import { useState, useCallback } from 'react';
import { 
  areExternalLinkWarningsDisabled,
  disableExternalLinkWarnings,
  resetExternalLinkPreferences
} from '../services/externalLinkPreferences';
import { openExternalLink } from '../utils/linkDetection';

/**
 * Custom hook for managing external link warnings
 * @returns {Object} Hook interface with warning state and methods
 */
export const useExternalLinkWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [targetUrl, setTargetUrl] = useState(null);

  /**
   * Check if warnings are currently disabled
   * @returns {boolean} True if warnings are disabled
   */
  const isWarningDisabled = useCallback(() => {
    return areExternalLinkWarningsDisabled();
  }, []);

  /**
   * Show warning modal for external link
   * @param {string} url - The external URL to warn about
   */
  const showExternalLinkWarning = useCallback((url) => {
    if (!url) return;

    // Check if warnings are disabled
    if (areExternalLinkWarningsDisabled()) {
      // Open link directly without warning
      openExternalLink(url);
      return;
    }

    // Show warning modal
    setTargetUrl(url);
    setShowWarning(true);
  }, []);

  /**
   * Handle user continuing to external link
   * @param {boolean} rememberChoice - Whether to remember the choice
   */
  const handleContinue = useCallback((rememberChoice = false) => {
    if (rememberChoice) {
      // Disable future warnings
      disableExternalLinkWarnings();
    }

    // Open the external link
    if (targetUrl) {
      openExternalLink(targetUrl);
    }

    // Close modal
    setShowWarning(false);
    setTargetUrl(null);
  }, [targetUrl]);

  /**
   * Handle user canceling the external link navigation
   */
  const handleCancel = useCallback(() => {
    setShowWarning(false);
    setTargetUrl(null);
  }, []);

  /**
   * Reset user preferences to show warnings again
   * @returns {boolean} True if successful
   */
  const resetPreferences = useCallback(() => {
    return resetExternalLinkPreferences();
  }, []);

  return {
    // State
    showWarning,
    targetUrl,
    isWarningDisabled: isWarningDisabled(),
    
    // Methods
    showExternalLinkWarning,
    handleContinue,
    handleCancel,
    resetPreferences
  };
};